<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import FormSection from "@/Components/FormSection.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import FrontendMediaUpload from "@/Components/Common/Media/FrontendMediaUpload.vue";
import { useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { ref } from "vue";

const props = defineProps({
  article: Object,
  employees: Array,
  initialMedia: Object,
  readOnly: {
    type: Boolean,
    default: false,
  }
});

const form = useForm({
  _method: props.article?.articleID ? "PUT" : "POST",
  title: props.article?.title || "",
  subject: props.article?.subject || "",
  article: props.article?.article || "",
  author: props.article?.author || "",
  keyword: props.article?.keyword || "",
  images: [],
  files: [],
});

const saveForm = () => {


  form.images = uploadedMedia.value.I
    .filter(item => !item.imageId && !item.id)
    .map(item => item.file);

  form.files = uploadedMedia.value.F
    .filter(item => !item.fileId && !item.id)
    .map(item => item.file);
    console.log(form);

    const routeName = props.article?.articleID
    ? route("admin.knowledgebase.update", { knowledgebase: props.article.articleID })
    : route("admin.knowledgebase.store");

  form.post(routeName, {
    preserveScroll: true,
    onSuccess: () => {
      form.reset();
      uploadedMedia.value = { I: [], F: [] };
      form.images = [];
      form.files = [];
    },
  });
};

const uploadedMedia = ref({
  I: (props.initialMedia?.I || []).map(item => ({
    ...item,
    imageId: item.id,
  })),
  F: (props.initialMedia?.F || []).map(item => ({
    ...item,
    fileId: item.id,
  })),
});

const mergeWithoutDuplicates = (existing, incoming) => {
  const fileNames = new Set(existing.map(file => file.file_name));
  const newFiles = incoming.filter(file => !fileNames.has(file.file_name));
  return [...existing, ...newFiles];
};

const handleMediaUpdate = (value) => {
  uploadedMedia.value = {
    I: mergeWithoutDuplicates(uploadedMedia.value.I, value.I),
    F: mergeWithoutDuplicates(uploadedMedia.value.F, value.F)
  };

  form.images = uploadedMedia.value.I
    .filter(item => !item.imageId && !item.id)
    .map(item => item.file);

  form.files = uploadedMedia.value.F
    .filter(item => !item.fileId && !item.id)
    .map(item => item.file);

};

const handleMediaDelete = (mediaItem, onSuccess, onError) => {
  const mediaId = mediaItem?.id;
  if (!mediaId) return;

  console.log(mediaId);

  Inertia.post(
    route("admin.knowledgebase.destroymedia", { knowledgebasemedia: mediaId }),
    { _method: "DELETE" },
    {
      preserveScroll: true,
      onSuccess: () => {
        uploadedMedia.value.I = uploadedMedia.value.I.filter(f => f.id !== mediaId);
        uploadedMedia.value.F = uploadedMedia.value.F.filter(f => f.id !== mediaId);
        form.images = uploadedMedia.value.I.map(item => item.file);
        form.files = uploadedMedia.value.F.map(item => item.file);
        if (onSuccess) onSuccess();
      },
      onError: () => {
        if (onError) onError();
      }
    }
  );
};
</script>

<template>
  <AppLayout :title="props.article?.articleID ? 'Edit Article' : 'New Article'">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        {{ props.readOnly ? 'View Article' : (props.article?.articleID ? 'Edit Article' : 'Create Article') }}
      </h2>
    </template>

    <div class="max-w-4xl mx-auto py-10 sm:px-6 lg:px-8">
      <FormSection gridClasses="grid-cols-1" :noGrid="true">
        <template #title>
          <a :href="route('admin.knowledgebase.index')">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <div class="flex flex-col gap-4">
            <div>
              <InputLabel value="Author" />
              <SelectInput v-model="form.author" :disabled="props.readOnly" class="mt-1">
                <option v-for="employee in employees" :key="employee.employeeID"
                        :value="`${employee.firstname} ${employee.lastname}`">
                  {{ employee.firstname }} {{ employee.lastname }}
                </option>
              </SelectInput>
            </div>

            <div>
              <InputLabel value="Title" />
              <TextInput v-model="form.title" class="w-full mt-1" :disabled="props.readOnly" />
            </div>

            <div>
              <InputLabel value="Keyword" />
              <TextInput v-model="form.keyword" class="w-full mt-1" :disabled="props.readOnly" />
            </div>

            <div>
              <InputLabel value="Subject" />
              <TextInput v-model="form.subject" class="w-full mt-1" :disabled="props.readOnly" />
            </div>

            <div>
              <InputLabel value="Article" />
              <textarea
                v-model="form.article"
                rows="10"
                :disabled="props.readOnly"
                class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full"
              ></textarea>
            </div>

            <div class="mt-4">
              <InputLabel v-if="!props.readOnly" value="Upload Media (Images or Files)" />
              <FrontendMediaUpload
                :initial-files="props.initialMedia"
                @update="handleMediaUpdate"
                @delete="handleMediaDelete"
                :can-see-upload-form="!props.readOnly"
                :can-modify-media-details="!props.readOnly"
              />
            </div>
          </div>
        </template>

        <template #actions>
          <PrimaryButton v-if="!props.readOnly" @click.prevent="saveForm">Save</PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
